import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Card, CardBody, CardHeader, Button } from "@/Components/ui";
import BreadCrumb from "@/Components/Common/BreadCrumb";
import { toast } from "react-hot-toast";
import { TableOrder, tableOrderService } from "@/api/services/table-order.service";

const statusLabels: Record<string, string> = {
  NEW: "신규",
  ACCEPTED: "조리중",
  SERVED: "완료",
  CANCELED: "취소",
};

const statusClasses: Record<string, string> = {
  NEW: "bg-amber-100 text-amber-800",
  ACCEPTED: "bg-sky-100 text-sky-800",
  SERVED: "bg-emerald-100 text-emerald-800",
  CANCELED: "bg-rose-100 text-rose-800",
};

const fulfillmentTypeLabels: Record<string, string> = {
  INSTANT: "즉시 제공",
  KITCHEN: "제조 필요",
};

const formatTableLabel = (order: TableOrder) => {
  if (order.table_name && order.table_code) {
    return `${order.table_name} (${order.table_code})`;
  }

  if (order.order_type === "pickup") {
    return "픽업 주문";
  }

  return "테이블 미지정";
};

const statusFilters = [
  { value: "", label: "전체" },
  { value: "NEW", label: "신규" },
  { value: "ACCEPTED", label: "조리중" },
  { value: "SERVED", label: "완료" },
];

const canCancelOrderFromStatusAction = (order: TableOrder) => {
  return (
    (order.status === "NEW" || order.status === "ACCEPTED") &&
    (order.payment_status === "UNPAID" || order.payment_status === "PAYMENT_FAILED")
  );
};

const REFRESH_OPTIONS = [5, 10, 15, 30];

const KitchenLauncher = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<TableOrder[]>([]);
  const [statusFilter, setStatusFilter] = useState("NEW");
  const [orderTypeFilter, setOrderTypeFilter] = useState("");
  const [autoRefreshSeconds, setAutoRefreshSeconds] = useState(10);
  const [loading, setLoading] = useState(false);
  const [updatingOrderId, setUpdatingOrderId] = useState<number | null>(null);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page_size: 100, fulfillment_type: "KITCHEN" };
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (orderTypeFilter) {
        params.order_type = orderTypeFilter;
      }

      const response = await tableOrderService.listTableOrders(params);
      setOrders(response.data.results || []);
    } catch (error) {
      toast.error("주방 주문 목록을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, [orderTypeFilter, statusFilter]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      void loadOrders();
    }, autoRefreshSeconds * 1000);

    return () => {
      window.clearInterval(timer);
    };
  }, [autoRefreshSeconds, loadOrders]);

  const handleChangeStatus = useCallback(
    async (order: TableOrder, nextStatus: "ACCEPTED" | "SERVED" | "CANCELED") => {
      setUpdatingOrderId(order.id);
      try {
        await tableOrderService.updateTableOrderStatus(order.id, nextStatus);
        await loadOrders();
        toast.success("주문 상태가 변경되었습니다.");
      } catch (error) {
        toast.error("주문 상태 변경에 실패했습니다.");
      } finally {
        setUpdatingOrderId(null);
      }
    },
    [loadOrders]
  );

  const groupedSummary = useMemo(() => {
    return {
      total: orders.length,
      newCount: orders.filter((order) => order.status === "NEW").length,
      acceptedCount: orders.filter((order) => order.status === "ACCEPTED").length,
      servedCount: orders.filter((order) => order.status === "SERVED").length,
    };
  }, [orders]);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="주방 런처" pageTitle="현장주문" />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4 mb-4">
          <Card>
            <CardBody>
              <p className="text-muted mb-2">표시 중 주문</p>
              <h3 className="mb-0">{groupedSummary.total}</h3>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-muted mb-2">신규</p>
              <h3 className="mb-0">{groupedSummary.newCount}</h3>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-muted mb-2">조리중</p>
              <h3 className="mb-0">{groupedSummary.acceptedCount}</h3>
            </CardBody>
          </Card>
          <Card>
            <CardBody>
              <p className="text-muted mb-2">완료</p>
              <h3 className="mb-0">{groupedSummary.servedCount}</h3>
            </CardBody>
          </Card>
        </div>

        <Card>
          <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div>
              <h5 className="mb-1">주방 주문 보드</h5>
              <p className="text-muted mb-0">신규 주문 확인, 조리 시작, 완료 처리를 빠르게 수행합니다.</p>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <div className="btn-group" role="group" aria-label="주문 상태 필터">
                {statusFilters.map((filter) => (
                  <Button
                    key={filter.value || "all"}
                    size="sm"
                    color={statusFilter === filter.value ? "primary" : "soft-secondary"}
                    onClick={() => setStatusFilter(filter.value)}
                  >
                    {filter.label}
                  </Button>
                ))}
              </div>
              <select
                className="form-select"
                value={orderTypeFilter}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setOrderTypeFilter(event.target.value)}
              >
                <option value="">전체 타입</option>
                <option value="serving">서빙</option>
                <option value="pickup">픽업</option>
              </select>
              <select
                className="form-select"
                value={String(autoRefreshSeconds)}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => setAutoRefreshSeconds(Number(event.target.value))}
              >
                {REFRESH_OPTIONS.map((seconds) => (
                  <option key={seconds} value={seconds}>
                    {seconds}초 새로고침
                  </option>
                ))}
              </select>
              <Button size="sm" color="soft-primary" onClick={() => void loadOrders()}>
                새로고침
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <p className="text-muted mb-0">불러오는 중...</p>
            ) : orders.length === 0 ? (
              <p className="text-muted mb-0">표시할 주문이 없습니다.</p>
            ) : (
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                {orders.map((order) => (
                  <Card key={order.id} className="border border-slate-200 shadow-sm">
                    <CardBody>
                      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClasses[order.status] || "bg-slate-100 text-slate-700"}`}>
                              {statusLabels[order.status] || order.status}
                            </span>
                            <span className="text-muted">주문 #{order.id}</span>
                          </div>
                          <h5 className="mb-1">{formatTableLabel(order)}</h5>
                          <p className="text-muted mb-0">
                            {fulfillmentTypeLabels[order.fulfillment_type || ""] || order.fulfillment_type || "제공 타입 미지정"} · {order.order_type === "pickup" ? "픽업" : "서빙"} · {order.created_by_phone}
                          </p>
                        </div>
                        <div className="text-end">
                          <p className="text-muted mb-1">총 금액</p>
                          <h5 className="mb-1">{order.total_amount.toLocaleString()}원</h5>
                          <small className="text-muted">{new Date(order.created_at).toLocaleString()}</small>
                        </div>
                      </div>

                      <div className="mb-3 rounded-3 bg-slate-50 p-3">
                        <div className="flex flex-col gap-2">
                          {order.items.map((item) => (
                            <div key={item.id} className="flex items-start justify-between gap-3 border-b border-slate-200 pb-2 last:border-b-0 last:pb-0">
                              <div>
                                <strong className="block">{item.menu_name_snapshot}</strong>
                                <small className="text-muted">
                                  {item.options.length === 0
                                    ? "옵션 없음"
                                    : item.options
                                        .map((option) => `${option.option_name_snapshot} x${option.quantity}`)
                                        .join(", ")}
                                </small>
                              </div>
                              <div className="text-end">
                                <strong>{item.quantity}개</strong>
                                <small className="block text-muted">{item.line_amount.toLocaleString()}원</small>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {order.memo ? <p className="mb-3 text-muted">메모: {order.memo}</p> : null}

                      <div className="d-flex flex-wrap gap-2">
                        <Button size="sm" color="soft-primary" onClick={() => navigate(`/table-order/order-detail/${order.id}`)}>
                          상세 보기
                        </Button>
                        {order.status === "NEW" ? (
                          <Button
                            size="sm"
                            color="success"
                            disabled={updatingOrderId === order.id}
                            onClick={() => void handleChangeStatus(order, "ACCEPTED")}
                          >
                            조리 시작
                          </Button>
                        ) : null}
                        {order.status === "ACCEPTED" ? (
                          <Button
                            size="sm"
                            color="info"
                            disabled={updatingOrderId === order.id}
                            onClick={() => void handleChangeStatus(order, "SERVED")}
                          >
                            조리 완료
                          </Button>
                        ) : null}
                        {(order.status === "NEW" || order.status === "ACCEPTED") ? (
                          <Button
                            size="sm"
                            color="soft-danger"
                            disabled={updatingOrderId === order.id || !canCancelOrderFromStatusAction(order)}
                            onClick={() => void handleChangeStatus(order, "CANCELED")}
                          >
                            주문 취소
                          </Button>
                        ) : null}
                      </div>
                    </CardBody>
                  </Card>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default KitchenLauncher;
